/**
 * The inspector: the focused row's fields as a form generated from the
 * domain's field schemas (`lib/editor/fieldForm.ts`), with slots below for
 * the kind's own panels (the typed piece form, the script).
 *
 * Each control keeps a local draft and commits on blur or Enter (a checkbox
 * or select on change); the draft is validated against the field's schema
 * before it is emitted, so a bad value is reported beside the field rather
 * than refused by the server. When the value changes underneath (a commit
 * coming back, another user), the control follows it unless the user has
 * typed something else — and it is never remounted, so it keeps focus and
 * the caret (`fieldDraft.ts`). Focus is reported per field
 * (`onFocusField`), which the view announces as presence.
 */
import type { RowKind } from '@acp/domain/fields.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { type KeyboardEvent, type ReactNode, useState } from 'react';
import { type FieldControl, parseFieldInput } from '../../lib/editor/fieldForm.js';
import type { RowPresence } from '../../lib/editor/presence.js';
import { cn } from '../../lib/utils.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { useFieldDraft } from './fieldDraft.js';
import { presenceClass } from './PresenceAvatars.js';

export type InspectorProps = {
  /** The focused row's kind, or null when nothing is focused. */
  readonly kind: RowKind | null;
  readonly heading: string;
  readonly controls: readonly FieldControl[];
  readonly presence: readonly RowPresence[];
  readonly onCommit: (path: string, value: JsonValue) => void;
  readonly onFocusField: (path: string | null) => void;
  readonly children?: ReactNode;
};

type ControlProps = {
  readonly kind: RowKind;
  readonly control: FieldControl;
  readonly editors: readonly RowPresence[];
  readonly onCommit: (path: string, value: JsonValue) => void;
  readonly onFocusField: (path: string | null) => void;
};

const SELECT_CLASS = 'h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

function Editors({ editors }: { readonly editors: readonly RowPresence[] }) {
  if (editors.length === 0) return null;
  return (
    <span className="ml-auto flex gap-1" data-testid="field-editors">
      {editors.map((p) => (
        <span key={p.userId} className={cn('rounded-sm px-1 text-xs', presenceClass(p.colour))} title={`${p.displayName} is editing this`}>
          {p.initials}
        </span>
      ))}
    </span>
  );
}

function BooleanControl({ control, kind, onCommit, onFocusField }: ControlProps) {
  const id = `field-${control.path}`;
  return (
    <input
      id={id}
      type="checkbox"
      className="size-4 accent-primary"
      checked={control.value === true}
      onFocus={() => onFocusField(control.path)}
      onBlur={() => onFocusField(null)}
      onChange={(e) => {
        const parsed = parseFieldInput(kind, control, e.target.checked);
        if (parsed.ok) onCommit(control.path, parsed.value);
      }}
    />
  );
}

function EnumControl({ control, kind, onCommit, onFocusField }: ControlProps) {
  return (
    <select
      id={`field-${control.path}`}
      className={SELECT_CLASS}
      value={typeof control.value === 'string' ? control.value : ''}
      onFocus={() => onFocusField(control.path)}
      onBlur={() => onFocusField(null)}
      onChange={(e) => {
        const parsed = parseFieldInput(kind, control, e.target.value);
        if (parsed.ok) onCommit(control.path, parsed.value);
      }}
    >
      {control.options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function TextControl({ control, kind, onCommit, onFocusField }: ControlProps) {
  const { draft, setDraft, dirty, markCommitted, rearm, reset } = useFieldDraft(typeof control.value === 'string' ? control.value : '');
  const [error, setError] = useState<string | null>(null);
  const commit = () => {
    if (!dirty) {
      setError(null);
      return;
    }
    const parsed = parseFieldInput(kind, control, draft);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    markCommitted(draft);
    onCommit(control.path, parsed.value);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      reset();
      setError(null);
    }
  };
  const placeholder = control.type === 'duration' ? 'm:ss' : control.type === 'json' ? 'JSON' : '';
  return (
    <>
      <Input
        id={`field-${control.path}`}
        className={cn('h-8', control.type !== 'text' && 'font-mono')}
        value={draft}
        placeholder={control.nullable ? `${placeholder} (none)`.trim() : placeholder}
        aria-invalid={error !== null}
        aria-describedby={error === null ? undefined : `field-${control.path}-error`}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          rearm();
          onFocusField(control.path);
        }}
        onBlur={() => {
          commit();
          onFocusField(null);
        }}
        onKeyDown={onKeyDown}
      />
      {error === null ? null : (
        <p id={`field-${control.path}-error`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function Control(props: ControlProps) {
  const { control, editors } = props;
  const inline = control.type === 'boolean';
  return (
    <div className={cn('grid gap-1', inline && 'flex items-center gap-2')} data-testid={`control-${control.path}`}>
      {inline ? <BooleanControl {...props} /> : null}
      <div className="flex items-center gap-2">
        <Label htmlFor={`field-${control.path}`} className="text-xs text-muted-foreground">
          {control.label}
        </Label>
        <Editors editors={editors} />
      </div>
      {control.type === 'enum' ? <EnumControl {...props} /> : null}
      {control.type === 'text' || control.type === 'duration' || control.type === 'json' ? <TextControl {...props} /> : null}
    </div>
  );
}

export function Inspector({ kind, heading, controls, presence, onCommit, onFocusField, children }: InspectorProps) {
  if (kind === null) {
    return (
      <aside className="p-4 text-sm text-muted-foreground" data-testid="inspector-empty" aria-label="Inspector">
        Select a row to edit its fields.
      </aside>
    );
  }
  return (
    <aside className="grid content-start gap-3 p-4" data-testid="inspector" aria-label="Inspector">
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{kind}</div>
        <h2 className="truncate text-base font-semibold" data-testid="inspector-heading">
          {heading}
        </h2>
      </div>
      {controls.map((control) => (
        <Control
          key={control.path}
          kind={kind}
          control={control}
          editors={presence.filter((p) => p.activity === 'editing' && p.field === control.path)}
          onCommit={onCommit}
          onFocusField={onFocusField}
        />
      ))}
      {children}
    </aside>
  );
}
